import React, { useState, useEffect } from 'react';
import InviteModal from './components/InviteModal';

import { db } from './firebase';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, orderBy } from './firestore-compat';
import { hasPermission } from './utils/roleUtils';
import { timeToMinutes, minutesToTime } from './utils/conflictUtils';
import AvailabilityPanel from './components/AvailabilityPanel';

// Define os responsáveis
const RESPONSAVEIS_OPCOES = {
  anaPaula: 'Ana Paula',
  drMario: 'Dr. Mário',
  diretoria: 'Diretoria'
};

function EditDirecaoModal({ isOpen, onClose, onUpdateEvento, onDeleteEvento, evento, userRole, userPermissions }) {

  // Estados para status
  const [showJustificativa, setShowJustificativa] = useState(false);
  const [justificativa, setJustificativa] = useState('');

  const [data, setData] = useState('');
  const [horario, setHorario] = useState('09:00');
  const [horarioFim, setHorarioFim] = useState('10:00');
  const [showAvailabilityPanel, setShowAvailabilityPanel] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [setor, setSetor] = useState('');
  const [participantes, setParticipantes] = useState('');
  const [systemUsers, setSystemUsers] = useState([]);
  const [selectedInternalParticipants, setSelectedInternalParticipants] = useState([]);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [generatedInviteText, setGeneratedInviteText] = useState('');
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);
  const [participantSearch, setParticipantSearch] = useState('');

  const [responsaveis, setResponsaveis] = useState({
    anaPaula: false,
    drMario: false,
    diretoria: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Este useEffect preenche o formulário quando o evento é carregado
  useEffect(() => {
    const fetchUsers = async () => {
      const q = query(collection(db, 'users'), orderBy('name'));
      const snap = await getDocs(q);
      setSystemUsers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (evento) {
      setData(evento.data || new Date().toISOString().split('T')[0]);
      setHorario(evento.horario || '09:00');
      setHorarioFim(evento.horarioFim || (evento.horario ? minutesToTime(timeToMinutes(evento.horario) + 60) : '10:00'));
      setDescricao(evento.descricao || '');
      setSetor(evento.setor || '');
      setParticipantes(evento.participantes || '');

      // Converte o array de responsáveis de volta para o formato de checkboxes
      const respObj = { anaPaula: false, drMario: false, diretoria: false };
      if (Array.isArray(evento.responsaveis)) {
        evento.responsaveis.forEach(nome => {
          if (nome === RESPONSAVEIS_OPCOES.anaPaula) respObj.anaPaula = true;
          if (nome === RESPONSAVEIS_OPCOES.drMario) respObj.drMario = true;
          if (nome === RESPONSAVEIS_OPCOES.diretoria) respObj.diretoria = true;
        });
      }
      setResponsaveis(respObj);
      setSelectedInternalParticipants(evento.participantesUids || []);
    }
  }, [evento]);

  if (!isOpen) return null;

  const handleHorarioChange = (e) => {
    const newHorario = e.target.value;
    setHorario(newHorario);
    if (newHorario) {
      setHorarioFim(minutesToTime(timeToMinutes(newHorario) + 60));
    }
  };

  const handleApplyTime = (newStart, newEnd) => {
    setHorario(newStart);
    setHorarioFim(newEnd);
  };

  const getDateStr = () => {
    if (typeof data === 'string') return data;
    if (data && typeof data.toISOString === 'function') return data.toISOString().split('T')[0];
    return '';
  };

  const toggleInternalParticipant = (userId) => {
    setSelectedInternalParticipants(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleResponsavelChange = (e) => {
    const { name, checked } = e.target;
    setResponsaveis(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const listaResponsaveis = Object.keys(responsaveis)
      .filter(key => responsaveis[key])
      .map(key => RESPONSAVEIS_OPCOES[key]);

    if (listaResponsaveis.length === 0) {
      alert('Selecione pelo menos um responsável.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Limpar objeto para atualização (não enviar ID no corpo dos dados)
      const { id, ...restoDoEvento } = evento;

      const eventoAtualizado = {
        ...restoDoEvento,
        data,
        horario,
        horarioFim,
        descricao,
        setor,
        participantes,
        participantesUids: selectedInternalParticipants,
        responsaveis: listaResponsaveis,
      };

      console.log(`[DEBUG] Tentando atualizar evento ${evento.id} na coleção agendaDirecao...`);
      await onUpdateEvento(evento.id, eventoAtualizado);
      onClose();
    } catch (error) {
      if (error.message === "CONFLITO_HORARIO") {
        alert("Já existe um agendamento neste dia e horário para um dos responsáveis selecionados. A edição foi cancelada.");
      } else if (error.message && error.message.includes("No document to update")) {
        // Se o erro for "no document to update", dar um aviso mais claro
        alert("Erro: O agendamento não foi encontrado no banco de dados. Ele pode ter sido excluído ou movido.");
      } else {
        console.error("Erro ao atualizar agendamento:", error);
        alert("Erro ao atualizar agendamento. Por favor, tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Tem certeza que deseja excluir este compromisso?')) {
      onDeleteEvento(evento.id);
      onClose();
    }
  };

  const handleDateChange = (e) => {
    const dateString = e.target.value;
    const [year, month, day] = dateString.split('-').map(Number);
    setData(new Date(year, month - 1, day).toISOString().split('T')[0]);
  };

  const handleGerarConvite = () => {
    const listaResponsaveis = Object.keys(responsaveis)
      .filter(key => responsaveis[key])
      .map(key => RESPONSAVEIS_OPCOES[key]);

    const dataFormatada = data.split('-').reverse().join('/');

    const textoConvite = `*Convite de Reunião*\n\n` +
      `*Assunto:* ${descricao}\n` +
      `*Data:* ${dataFormatada}\n` +
      `*Horário:* ${horario}\n` +
      (setor ? `*Setor:* ${setor}\n` : '') +
      (participantes ? `*Participantes:* ${participantes}\n` : '') +
      `*Responsáveis:* ${listaResponsaveis.join(', ')}`;

    setGeneratedInviteText(textoConvite);
    setShowInviteModal(true);
  };

  const handleMarcarRealizado = async () => {
    setIsSubmitting(true);
    try {
      await onUpdateEvento(evento.id, {
        status: 'Feito',
        dataConclusao: new Date().toISOString().split('T')[0],
        justificativaNaoRealizado: null
      });

      // --- NOVO: Propagar o status para a agenda_pessoal ---
      try {
        const originalId = evento?.sourceRef || evento?.originalEventId || evento.id;
        const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', originalId));
        const snapPes = await getDocs(qPes);
        snapPes.forEach(async (d) => {
          await updateDoc(doc(db, 'agenda_pessoal', d.id), {
            status: 'Feito',
            dataConclusao: new Date().toISOString().split('T')[0],
            justificativaNaoRealizado: null
          });
        });
      } catch (pesErr) {
        console.error('[STATUS] Erro ao propagar status para agenda_pessoal:', pesErr);
      }
      // --------------------------------------------------------

      onClose();
    } catch (error) {
      console.error("Erro ao marcar como realizado:", error);
      alert("Erro ao atualizar status. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarcarNaoRealizado = () => {
    setShowJustificativa(true);
  };

  const handleSalvarJustificativa = async () => {
    if (!justificativa.trim()) {
      alert('Por favor, informe a justificativa.');
      return;
    }
    setIsSubmitting(true);
    try {
      await onUpdateEvento(evento.id, {
        status: 'Não Realizado',
        justificativaNaoRealizado: justificativa,
        dataConclusao: null
      });

      // --- NOVO: Propagar justificativa para a agenda_pessoal ---
      try {
        const originalId = evento?.sourceRef || evento?.originalEventId || evento.id;
        const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', originalId));
        const snapPes = await getDocs(qPes);
        snapPes.forEach(async (d) => {
          await updateDoc(doc(db, 'agenda_pessoal', d.id), {
            status: 'Não Realizado',
            justificativaNaoRealizado: justificativa,
            dataConclusao: null
          });
        });
      } catch (pesErr) {
        console.error('[STATUS] Erro ao propagar justificativa para agenda_pessoal:', pesErr);
      }
      // --------------------------------------------------------

      setShowJustificativa(false);
      setJustificativa('');
      onClose();
    } catch (error) {
      console.error("Erro ao salvar justificativa:", error);
      alert("Erro ao salvar justificativa. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Editar Compromisso (Direção)</h2>
        <form onSubmit={handleSubmit}>

          <div className="form-group-row">
            <div className="form-group">
              <label>Data</label>
              <input type="date" value={data} onChange={handleDateChange} required />
            </div>
            <div className="form-group">
              <label>Horário Inicial</label>
              <input type="time" value={horario} onChange={handleHorarioChange} required />
            </div>
            <div className="form-group">
              <label>Horário Final</label>
              <input type="time" value={horarioFim} onChange={(e) => setHorarioFim(e.target.value)} required />
            </div>
          </div>


          <div className="form-group">
            <label>Descrição do Compromisso</label>
            <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)} required autoFocus />
          </div>

          <div className="form-group">
            <label>Responsáveis</label>
            <div className="checkbox-group">
              {[{ key: 'anaPaula', label: 'Ana Paula' }, { key: 'drMario', label: 'Dr. Mário' }, { key: 'diretoria', label: 'Diretoria (Outros)' }].map(({ key, label }) => (
                <label key={key} className="checkbox-label" style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  marginBottom: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: responsaveis[key] ? '#26a69a' : '#f0f0f0',
                  color: responsaveis[key] ? '#fff' : '#333',
                  border: responsaveis[key] ? '2px solid #00796b' : '2px solid transparent',
                  fontWeight: responsaveis[key] ? '600' : 'normal',
                  transition: 'all 0.15s ease',
                  marginRight: '8px'
                }}>
                  <input
                    type="checkbox"
                    name={key}
                    checked={responsaveis[key]}
                    onChange={handleResponsavelChange}
                    style={{ display: 'none' }}
                  />
                  {responsaveis[key] ? '✓ ' : ''}{label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Setor Envolvido (Opcional)</label>
            <input type="text" value={setor} onChange={(e) => setSetor(e.target.value)} />
          </div>

          {/* Participantes Internos - com picker popup */}
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ margin: 0 }}>Participantes Internos</label>
              <button
                type="button"
                onClick={() => { setParticipantSearch(''); setShowParticipantPicker(true); }}
                style={{
                  fontSize: '0.8rem', padding: '4px 12px', borderRadius: '20px',
                  backgroundColor: '#00897b', color: '#fff', border: 'none', cursor: 'pointer'
                }}
              >
                + Selecionar
              </button>
            </div>
            {/* Tags dos selecionados */}
            <div style={{
              minHeight: '40px', border: '1px solid #ddd', borderRadius: '4px',
              padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', backgroundColor: '#fafafa'
            }}>
              {selectedInternalParticipants.length === 0 ? (
                <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Nenhum participante selecionado</span>
              ) : (
                selectedInternalParticipants.map(uid => {
                  const user = systemUsers.find(u => u.id === uid);
                  if (!user) return null;
                  return (
                    <span key={uid} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      backgroundColor: '#e0f2f1', border: '1px solid #26a69a',
                      borderRadius: '20px', padding: '3px 10px', fontSize: '0.82rem',
                      color: '#00695c', fontWeight: '500'
                    }}>
                      {user.name}
                      <span onClick={() => toggleInternalParticipant(uid)}
                        style={{ cursor: 'pointer', fontWeight: 'bold', opacity: 0.7, marginLeft: '2px' }}
                      >×</span>
                    </span>
                  );
                })
              )}
            </div>
          </div>

          {/* Botão Verificar Agendas */}
          {selectedInternalParticipants.length > 0 && (
            <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
              <button
                type="button"
                onClick={() => setShowAvailabilityPanel(true)}
                style={{
                  background: 'linear-gradient(135deg, #00695c, #00897b)',
                  color: '#fff', border: 'none', borderRadius: '25px',
                  padding: '10px 24px', fontWeight: 700, fontSize: '0.9rem',
                  cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,80,157,0.3)',
                  display: 'inline-flex', alignItems: 'center', gap: '8px'
                }}
              >
                🔍 Verificar Agendas Disponíveis
              </button>
            </div>
          )}

          <div className="form-group">
            <label>Outros Participantes / Observações (Opcional)</label>
            <textarea
              placeholder="Digite os nomes dos participantes externos ou observações relevantes..."
              value={participantes}
              onChange={(e) => setParticipantes(e.target.value)}
              rows="2"
            ></textarea>
          </div>

          {/* PAINEL DE RSVP (CONFIRMAÇÕES) */}
          {evento && (evento.participantesUids?.length > 0 || evento.aceitePorUids?.length > 0 || evento.recusas?.length > 0) && (
            <div style={{
              margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef', borderRadius: '8px'
            }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📋 Painel de Confirmações (RSVP)
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* Confirmados */}
                {(() => {
                  const acceptedIds = evento.aceitePorUids || [];
                  const acceptedNames = evento.aceitePor || [];
                  const confirmedList = [...new Set([
                    ...acceptedIds.map(uid => {
                      const u = systemUsers.find(su => su.id === uid);
                      return u ? u.name : null;
                    }).filter(Boolean),
                    ...acceptedNames
                  ])];

                  return confirmedList.length > 0 ? (
                    <div>
                      <strong style={{ color: '#28a745', fontSize: '0.85rem' }}>✅ Confirmados ({confirmedList.length}):</strong>
                      <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>{confirmedList.join(', ')}</div>
                    </div>
                  ) : null;
                })()}

                {/* Recusados */}
                {(() => {
                  const recusas = evento.recusas || [];
                  return recusas.length > 0 ? (
                    <div>
                      <strong style={{ color: '#dc3545', fontSize: '0.85rem' }}>❌ Recusados ({recusas.length}):</strong>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                        {recusas.map((r, idx) => (
                          <div key={idx} style={{ fontSize: '0.85rem', color: '#555', backgroundColor: '#ffe6e6', padding: '4px 8px', borderRadius: '4px' }}>
                            <b>{r.nome}:</b> {r.justificativa || 'Sem justificativa'}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Pendentes */}
                {(() => {
                  const allUids = evento.participantesUids || [];
                  const acceptedIds = evento.aceitePorUids || [];
                  const recusasIds = (evento.recusas || []).map(r => r.uid);
                  
                  const pendingUids = allUids.filter(uid => !acceptedIds.includes(uid) && !recusasIds.includes(uid));
                  
                  if (pendingUids.length === 0) return null;

                  const pendingNames = pendingUids.map(uid => {
                    const u = systemUsers.find(su => su.id === uid);
                    return u ? u.name : 'Usuário Desconhecido';
                  });

                  return (
                    <div>
                      <strong style={{ color: '#fd7e14', fontSize: '0.85rem' }}>⏳ Aguardando Resposta ({pendingNames.length}):</strong>
                      <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px' }}>{pendingNames.join(', ')}</div>
                    </div>
                  );
                })()}
                
              </div>
            </div>
          )}

          <div className="modal-actions" style={{ flexWrap: 'wrap', gap: '8px' }}>
            {/* Botões de Status */}
            <button type="button" onClick={handleMarcarRealizado} style={{
              backgroundColor: '#28a745', color: 'white', border: 'none',
              padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'
            }}>✅ Marcar Realizado</button>
            <button type="button" onClick={handleMarcarNaoRealizado} style={{
              backgroundColor: '#dc3545', color: 'white', border: 'none',
              padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'
            }}>❌ Não Realizado</button>

            <div style={{ flexGrow: 1 }}></div>

            {/* Botões originais */}
            <button type="button" onClick={handleDelete} className="btn-excluir">Excluir</button>
            <button type="button" onClick={handleGerarConvite} className="btn-save" style={{ backgroundColor: '#00897b' }}>Gerar Convite</button>
            <button
              type="submit"
              className="btn-save"
              style={{ flex: 1, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </button>
            <button type="button" onClick={onClose} className="btn-cancel" disabled={isSubmitting}>Cancelar</button>
          </div>

          {/* Modal de Justificativa para Não Realizado */}
          {showJustificativa && (
            <div style={{
              marginTop: '15px', padding: '15px', backgroundColor: '#fff3cd',
              border: '1px solid #ffc107', borderRadius: '8px'
            }}>
              <h4 style={{ margin: '0 0 10px', color: '#856404' }}>Justificativa - Não Realizado</h4>
              <textarea
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                placeholder="Informe o motivo pelo qual o compromisso não foi realizado..."
                rows="3"
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setShowJustificativa(false); setJustificativa(''); }}
                  style={{ padding: '6px 16px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer' }}
                >Cancelar</button>
                <button type="button" onClick={handleSalvarJustificativa}
                  style={{ padding: '6px 16px', borderRadius: '4px', backgroundColor: '#dc3545', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                >Confirmar</button>
              </div>
            </div>
          )}
        </form>
      </div>
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        inviteText={generatedInviteText}
      />

      {/* ===== PARTICIPANT PICKER POPUP ===== */}
      {showParticipantPicker && (
        <div className="modal-overlay" style={{ zIndex: 3000 }} onClick={() => setShowParticipantPicker(false)}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '95%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '12px', fontSize: '1.1rem', color: '#333' }}>Selecionar Participantes</h3>
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={participantSearch}
              onChange={e => setParticipantSearch(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '12px', fontSize: '0.9rem' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, border: '1px solid #eee', borderRadius: '6px' }}>
              {systemUsers
                .filter(u => {
                  const q = participantSearch.toLowerCase();
                  return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                })
                .map(u => {
                  const isSelected = selectedInternalParticipants.includes(u.id);
                  return (
                    <label key={u.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', cursor: 'pointer',
                      backgroundColor: isSelected ? '#e0f2f1' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background 0.15s'
                    }}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleInternalParticipant(u.id)} />
                      <div>
                        <div style={{ fontWeight: isSelected ? '600' : 'normal', color: isSelected ? '#00695c' : '#333', fontSize: '0.9rem' }}>
                          {u.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#888' }}>{u.email}</div>
                      </div>
                    </label>
                  );
                })
              }
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <span style={{ fontSize: '0.85rem', color: '#555', alignSelf: 'center' }}>
                {selectedInternalParticipants.length} selecionado(s)
              </span>
              <button type="button" onClick={() => setShowParticipantPicker(false)}
                style={{ padding: '8px 20px', borderRadius: '6px', backgroundColor: '#00897b', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: '600' }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      <AvailabilityPanel
        isOpen={showAvailabilityPanel}
        onClose={() => setShowAvailabilityPanel(false)}
        date={getDateStr()}
        startTime={horario}
        endTime={horarioFim}
        participantsUids={selectedInternalParticipants}
        systemUsers={systemUsers}
        excludeEventId={evento ? evento.id : null}
        onApplyTime={handleApplyTime}
      />
    </div>
  );
}

export default EditDirecaoModal;